import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import AddIcon from '@mui/icons-material/AddOutlined';
import { AddOtherDocumentsDialog } from '../../components/AddOtherDocumentsDialog';
import { DOCUMENT_ACCEPT, documentFileProblem } from '../../utils/documentNames';
import { getFinalDocumentChecklist, setFinalDocumentNotApplicable } from '../../api/finalDocumentation';
import { downloadStudentDocument, previewStudentDocumentUrl, uploadStudentDocument } from '../../api/students';
import { useAuth } from '../../context/authContext';
import { DocumentPreviewDialog, DocumentPreviewTarget } from '../../components/DocumentPreviewDialog';
import { downloadBlob } from '../../utils/downloadBlob';
import {
  FinalDocumentChecklist,
  FinalDocumentItem,
  FinalDocumentItemStatus,
  FinalDocumentVersion,
  OtherDocumentEntry,
} from '../../types/finalDocumentation';
import { invalidateStudentDocumentViews, useDocumentReview } from '../students/useDocumentReview';
import { RejectDocumentDialog } from '../students/RejectDocumentDialog';

const errorMessage = (err: unknown) =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong') : 'Something went wrong';

const personName = (p: { firstName: string; lastName: string } | null) =>
  p ? `${p.firstName} ${p.lastName}` : '';

const formatDate = (iso: string) => new Date(iso).toLocaleString();

const finalDocumentChecklistQueryKey = (studentId: number) => ['final-document-checklist', studentId];

const FINAL_STATUS: Record<
  FinalDocumentItemStatus,
  { label: string; color: 'default' | 'success' | 'warning' | 'error' }
> = {
  NOT_APPLICABLE: { label: 'Not applicable', color: 'default' },
  MISSING: { label: 'Not uploaded', color: 'default' },
  PENDING: { label: 'Pending review', color: 'warning' },
  APPROVED: { label: 'Approved', color: 'success' },
  REJECTED: { label: 'Rejected', color: 'error' },
};

const EMPTY_OTHER_DOCUMENTS = { number: '10', title: 'OTHER DOCUMENTS', documentType: null, entries: [] };

const DECISION_LABEL: Record<FinalDocumentVersion['verificationStatus'], string> = {
  PENDING: 'Not reviewed',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

/** Final Documentation Checklist (Phase 95) — the "2nd List Document Check
 * List". Document-based since Phase 99: each item is uploaded as its own
 * document type, reviewed (approved, or rejected with a reason) like any
 * other student document, and complete only once its document is approved.
 * Conditional items can be marked not applicable. Replacements keep every
 * earlier version and its decision. Reviewing needs documents:verify (a
 * Counselor only for their own students — the API enforces it); uploading
 * needs documents:upload; not applicable needs ready_to_fly:manage or
 * documents:verify. */
export const FinalDocumentChecklistPanel = ({ studentId }: { studentId: number }) => {
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [searchParams] = useSearchParams();
  const highlightDocumentId = Number(searchParams.get('documentId')) || null;
  const canReview = hasPermission('documents', 'verify');
  const canUpload = hasPermission('documents', 'upload');
  const canPreview = hasPermission('documents', 'view');
  const canDownload = hasPermission('documents', 'download');
  const canSetNotApplicable = hasPermission('ready_to_fly', 'manage') || canReview;
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** What the hidden file input uploads for: a standard item, or an Other
   * entry (by name) being replaced. */
  const [uploadFor, setUploadFor] = useState<
    { key: string; documentTypeId: number; customName?: string } | null
  >(null);
  const [addOtherOpen, setAddOtherOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<{ id: number; label: string } | null>(null);
  const [previewTarget, setPreviewTarget] = useState<DocumentPreviewTarget | null>(null);
  const [openHistory, setOpenHistory] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: finalDocumentChecklistQueryKey(studentId),
    queryFn: () => getFinalDocumentChecklist(studentId),
  });

  const notApplicableMutation = useMutation({
    mutationFn: ({ itemKey, value }: { itemKey: string; value: boolean }) =>
      setFinalDocumentNotApplicable(studentId, itemKey, value),
    onSuccess: (checklist: FinalDocumentChecklist) => {
      queryClient.setQueryData(finalDocumentChecklistQueryKey(studentId), checklist);
      invalidateStudentDocumentViews(queryClient, studentId);
      setActionError(null);
    },
    onError: (err) => setActionError(errorMessage(err)),
  });

  // Sections where every item is conditional (3. Local / 4. London
  // certificates): one click marks the whole section (not) applicable, one
  // audited item at a time — e.g. London for a student with local results.
  const sectionNotApplicableMutation = useMutation({
    mutationFn: async ({ itemKeys, value }: { itemKeys: string[]; value: boolean }) => {
      let checklist: FinalDocumentChecklist | null = null;
      for (const itemKey of itemKeys) checklist = await setFinalDocumentNotApplicable(studentId, itemKey, value);
      return checklist;
    },
    onSuccess: (checklist) => {
      if (checklist) queryClient.setQueryData(finalDocumentChecklistQueryKey(studentId), checklist);
      invalidateStudentDocumentViews(queryClient, studentId);
      setActionError(null);
    },
    onError: (err) => {
      setActionError(errorMessage(err));
      void queryClient.invalidateQueries({ queryKey: finalDocumentChecklistQueryKey(studentId) });
    },
  });

  const uploadMutation = useMutation({
    mutationFn: ({ file, target }: { file: File; target: NonNullable<typeof uploadFor> }) =>
      uploadStudentDocument(
        studentId,
        file,
        target.documentTypeId,
        target.customName ? { customName: target.customName, newEntry: false } : {},
      ),
    onSuccess: () => setActionError(null),
    onError: (err) => setActionError(errorMessage(err)),
    onSettled: () => {
      setUploadFor(null);
      invalidateStudentDocumentViews(queryClient, studentId);
    },
  });

  const review = useDocumentReview(studentId, () => {
    setRejectTarget(null);
    setActionError(null);
  });

  if (query.isLoading) return <CircularProgress size={24} />;
  if (query.isError || !query.data) {
    return <Alert severity="error">Couldn't load the Final Documentation Checklist.</Alert>;
  }

  const { sections, progress } = query.data;
  // An API from before Phase 103 sends no otherDocuments.
  const otherDocuments = query.data.otherDocuments ?? EMPTY_OTHER_DOCUMENTS;

  const download = async (documentId: number) => {
    try {
      const { blob, filename } = await downloadStudentDocument(documentId);
      downloadBlob(blob, filename);
    } catch (err) {
      setActionError(errorMessage(err));
    }
  };
  const openPreview = (doc: FinalDocumentVersion) =>
    setPreviewTarget({
      key: doc.id,
      originalName: doc.originalName,
      mimeType: doc.mimeType,
      fetchBlob: async () => (await previewStudentDocumentUrl(doc.id)).blob,
      onDownload: () => void download(doc.id),
    });

  const fileActions = (doc: FinalDocumentVersion) => (
    <>
      {canPreview && (
        <Tooltip title="Preview">
          <IconButton size="small" aria-label={`Preview ${doc.originalName}`} onClick={() => openPreview(doc)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {canDownload && (
        <Tooltip title="Download">
          <IconButton size="small" aria-label={`Download ${doc.originalName}`} onClick={() => void download(doc.id)}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  const decisionLine = (doc: FinalDocumentVersion) => (
    <>
      <Typography variant="caption" color="text.secondary" display="block">
        {doc.originalName} · version {doc.version} · uploaded by {personName(doc.uploadedBy)} on{' '}
        {formatDate(doc.createdAt)}
      </Typography>
      {doc.verifiedBy && doc.verifiedAt && doc.verificationStatus !== 'PENDING' && (
        <Typography
          variant="caption"
          display="block"
          color={doc.verificationStatus === 'REJECTED' ? 'error.main' : 'success.main'}
        >
          {DECISION_LABEL[doc.verificationStatus]} by {personName(doc.verifiedBy)} on {formatDate(doc.verifiedAt)}
          {doc.verificationStatus === 'REJECTED' && doc.rejectionReason && ` — Reason: ${doc.rejectionReason}`}
        </Typography>
      )}
    </>
  );

  const renderItem = (item: FinalDocumentItem) => {
    const doc = item.document;
    const highlighted =
      !!highlightDocumentId &&
      (doc?.id === highlightDocumentId || item.history.some((h) => h.id === highlightDocumentId));
    const historyOpen = openHistory.has(item.key);
    const uploading = uploadMutation.isPending && uploadFor?.key === item.key;
    const reviewable = !!doc && item.status !== 'NOT_APPLICABLE';
    return (
      <Box
        key={item.key}
        data-testid={`final-doc-item-${item.key}`}
        data-highlighted={highlighted ? 'true' : undefined}
        sx={{
          opacity: item.notApplicable ? 0.6 : 1,
          p: highlighted ? 1 : 0,
          borderRadius: 1,
          outline: highlighted ? '2px solid' : 'none',
          outlineColor: 'warning.main',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">
              {item.letter ? `${item.letter}) ` : ''}
              {item.text}
              {item.referenceUrl && (
                <>
                  {' '}
                  <Link href={item.referenceUrl} target="_blank" rel="noopener noreferrer">
                    {item.referenceUrl}
                  </Link>
                </>
              )}
            </Typography>
            {doc && decisionLine(doc)}
            {!item.documentType && (
              <Typography variant="caption" color="error">
                No document type is linked to this item — ask an administrator.
              </Typography>
            )}
          </Box>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ flexShrink: 0 }}
          >
            <Chip size="small" label={FINAL_STATUS[item.status].label} color={FINAL_STATUS[item.status].color} />
            {doc && fileActions(doc)}
            {reviewable && canReview && item.status !== 'APPROVED' && (
              <Tooltip title="Approve">
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    aria-label={`Approve ${item.key}`}
                    disabled={review.isPending}
                    onClick={() => review.mutate({ documentId: doc.id, status: 'APPROVED' })}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {reviewable && canReview && item.status !== 'REJECTED' && (
              <Tooltip title="Reject">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={`Reject ${item.key}`}
                    disabled={review.isPending}
                    onClick={() => {
                      review.reset();
                      setRejectTarget({ id: doc.id, label: item.documentType?.name ?? item.text });
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canUpload && item.documentType && item.status !== 'APPROVED' && item.status !== 'NOT_APPLICABLE' && (
              <Button
                size="small"
                startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
                disabled={uploadMutation.isPending}
                aria-label={`${doc ? 'Upload replacement for' : 'Upload'} ${item.key}`}
                onClick={() => {
                  setUploadFor({ key: item.key, documentTypeId: item.documentType!.id });
                  fileInputRef.current?.click();
                }}
              >
                {doc ? 'Replace' : 'Upload'}
              </Button>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          {item.conditional && (
            <FormControlLabel
              label="Not applicable"
              disabled={!canSetNotApplicable || notApplicableMutation.isPending || sectionNotApplicableMutation.isPending}
              control={
                <Checkbox
                  size="small"
                  checked={item.notApplicable}
                  inputProps={{ 'aria-label': `${item.key} Not applicable` }}
                  onChange={(e) => notApplicableMutation.mutate({ itemKey: item.key, value: e.target.checked })}
                />
              }
            />
          )}
          {item.notApplicable && item.notApplicableUpdatedBy && item.notApplicableUpdatedAt && (
            <Typography variant="caption" color="text.secondary">
              Marked not applicable by {personName(item.notApplicableUpdatedBy)} ·{' '}
              {formatDate(item.notApplicableUpdatedAt)}
            </Typography>
          )}
          {item.history.length > 0 && (
            <Button
              size="small"
              startIcon={<HistoryIcon />}
              onClick={() =>
                setOpenHistory((prev) => {
                  const next = new Set(prev);
                  if (next.has(item.key)) next.delete(item.key);
                  else next.add(item.key);
                  return next;
                })
              }
            >
              {historyOpen ? 'Hide' : 'Show'} earlier versions ({item.history.length})
            </Button>
          )}
        </Stack>
        <Collapse in={historyOpen} unmountOnExit>
          <Stack spacing={0.5} sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider', mb: 1 }}>
            {item.history.map((h) => (
              <Stack key={h.id} direction="row" alignItems="center" spacing={1}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  {decisionLine(h)}
                  {h.verificationStatus === 'PENDING' && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Replaced before it was reviewed
                    </Typography>
                  )}
                </Box>
                {fileActions(h)}
              </Stack>
            ))}
          </Stack>
        </Collapse>
      </Box>
    );
  };

  /** "10. Other Documents" (Phase 103): one entry per name the student
   * gave, reviewed exactly like a standard item. */
  const renderOtherEntry = (entry: OtherDocumentEntry) => {
    const doc = entry.document;
    const key = `other-${entry.name.toLowerCase()}`;
    const highlighted =
      !!highlightDocumentId && (doc.id === highlightDocumentId || entry.history.some((h) => h.id === highlightDocumentId));
    const historyOpen = openHistory.has(key);
    const uploading = uploadMutation.isPending && uploadFor?.key === key;
    return (
      <Box
        key={key}
        data-testid={`final-doc-other-${doc.id}`}
        data-highlighted={highlighted ? 'true' : undefined}
        sx={{
          p: highlighted ? 1 : 0,
          borderRadius: 1,
          outline: highlighted ? '2px solid' : 'none',
          outlineColor: 'warning.main',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={500}>
              {entry.name}
            </Typography>
            {decisionLine(doc)}
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
            <Chip size="small" label={FINAL_STATUS[entry.status].label} color={FINAL_STATUS[entry.status].color} />
            {fileActions(doc)}
            {canReview && entry.status !== 'APPROVED' && (
              <Tooltip title="Approve">
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    aria-label={`Approve ${entry.name}`}
                    disabled={review.isPending}
                    onClick={() => review.mutate({ documentId: doc.id, status: 'APPROVED' })}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canReview && entry.status !== 'REJECTED' && (
              <Tooltip title="Reject">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label={`Reject ${entry.name}`}
                    disabled={review.isPending}
                    onClick={() => {
                      review.reset();
                      setRejectTarget({ id: doc.id, label: `Other document — ${entry.name}` });
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {canUpload && otherDocuments.documentType && entry.status !== 'APPROVED' && (
              <Button
                size="small"
                startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
                disabled={uploadMutation.isPending}
                aria-label={`Upload replacement for ${entry.name}`}
                onClick={() => {
                  setUploadFor({ key, documentTypeId: otherDocuments.documentType!.id, customName: entry.name });
                  fileInputRef.current?.click();
                }}
              >
                Replace
              </Button>
            )}
          </Stack>
        </Stack>
        {entry.history.length > 0 && (
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() =>
              setOpenHistory((prev) => {
                const next = new Set(prev);
                if (next.has(key)) next.delete(key);
                else next.add(key);
                return next;
              })
            }
          >
            {historyOpen ? 'Hide' : 'Show'} earlier versions ({entry.history.length})
          </Button>
        )}
        <Collapse in={historyOpen} unmountOnExit>
          <Stack spacing={0.5} sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider', mb: 1 }}>
            {entry.history.map((h) => (
              <Stack key={h.id} direction="row" alignItems="center" spacing={1}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>{decisionLine(h)}</Box>
                {fileActions(h)}
              </Stack>
            ))}
          </Stack>
        </Collapse>
      </Box>
    );
  };

  const otherApproved = otherDocuments.entries.filter((e) => e.status === 'APPROVED').length;

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          color={progress.complete ? 'success' : progress.approved > 0 ? 'warning' : 'default'}
          label={`Approved: ${progress.approved}/${progress.total}`}
        />
        <Chip variant="outlined" label={`Uploaded: ${progress.uploaded}/${progress.total}`} />
        {progress.pending > 0 && (
          <Chip variant="outlined" color="warning" label={`Pending review: ${progress.pending}`} />
        )}
        {progress.rejected > 0 && <Chip variant="outlined" color="error" label={`Rejected: ${progress.rejected}`} />}
        {progress.missing > 0 && <Chip variant="outlined" label={`Not uploaded: ${progress.missing}`} />}
        {progress.complete && <Chip color="success" variant="outlined" label="Complete" />}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Each item is complete only when its uploaded document has been approved. Counts include only the
        items that apply to this student.
        {!canReview && ' You can view this checklist but not review documents.'}
      </Typography>
      {actionError && (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {review.isError && !rejectTarget && (
        <Alert severity="error" onClose={() => review.reset()}>
          {errorMessage(review.error)}
        </Alert>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={DOCUMENT_ACCEPT}
        data-testid="final-doc-file"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file || !uploadFor) return;
          const problem = documentFileProblem(file);
          if (problem) {
            setUploadFor(null);
            setActionError(problem);
            return;
          }
          uploadMutation.mutate({ file, target: uploadFor });
        }}
      />

      <Box>
        {sections.map((section) => {
          const applicable = section.items.filter((i) => !i.notApplicable);
          const done = applicable.filter((i) => i.status === 'APPROVED').length;
          return (
            <Accordion key={section.number} defaultExpanded disableGutters variant="outlined">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', pr: 1 }}>
                  <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                    {section.number}. {section.title}
                    {section.note && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({section.note})
                      </Typography>
                    )}
                  </Typography>
                  <Chip
                    size="small"
                    color={applicable.length > 0 && done === applicable.length ? 'success' : 'default'}
                    label={applicable.length === 0 ? 'N/A' : `${done}/${applicable.length}`}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {canSetNotApplicable && section.items.length > 1 && section.items.every((i) => i.conditional) && (
                  <Box sx={{ mb: 1.5 }}>
                    {(() => {
                      const allNotApplicable = section.items.every((i) => i.notApplicable);
                      const toChange = section.items.filter((i) => i.notApplicable !== !allNotApplicable).map((i) => i.key);
                      return (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={sectionNotApplicableMutation.isPending || notApplicableMutation.isPending}
                          onClick={() => sectionNotApplicableMutation.mutate({ itemKeys: toChange, value: !allNotApplicable })}
                        >
                          {allNotApplicable ? 'Mark whole section applicable' : 'Mark whole section not applicable'}
                        </Button>
                      );
                    })()}
                  </Box>
                )}
                <Stack spacing={2}>{section.items.map(renderItem)}</Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}
        <Accordion defaultExpanded disableGutters variant="outlined" data-testid="final-doc-other-section">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', pr: 1 }}>
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                {otherDocuments.number}. {otherDocuments.title}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  (named by the student; not counted in the progress above)
                </Typography>
              </Typography>
              <Chip
                size="small"
                label={otherDocuments.entries.length === 0 ? 'None' : `${otherApproved}/${otherDocuments.entries.length}`}
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {otherDocuments.entries.map(renderOtherEntry)}
              {otherDocuments.entries.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No other documents have been added.
                </Typography>
              )}
              {canUpload && otherDocuments.documentType && (
                <Box>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOtherOpen(true)}>
                    Add other documents
                  </Button>
                </Box>
              )}
              {!otherDocuments.documentType && (
                <Typography variant="caption" color="error">
                  The Other Documents type isn't set up yet — the Phase 103 database migration needs to be run.
                </Typography>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
      {otherDocuments.documentType && (
        <AddOtherDocumentsDialog
          open={addOtherOpen}
          existingNames={otherDocuments.entries.map((e) => e.name)}
          onClose={() => setAddOtherOpen(false)}
          upload={(name, file, onProgress) =>
            uploadStudentDocument(studentId, file, otherDocuments.documentType!.id, {
              customName: name,
              newEntry: true,
              onProgress,
            })
          }
          onUploaded={() => invalidateStudentDocumentViews(queryClient, studentId)}
        />
      )}

      <RejectDocumentDialog
        target={rejectTarget}
        pending={review.isPending}
        error={review.isError ? errorMessage(review.error) : null}
        onCancel={() => setRejectTarget(null)}
        onConfirm={(reason) =>
          rejectTarget && review.mutate({ documentId: rejectTarget.id, status: 'REJECTED', reason })
        }
      />
      <DocumentPreviewDialog target={previewTarget} onClose={() => setPreviewTarget(null)} />
    </Stack>
  );
};
